<template>
  <el-drawer
    v-model="visible"
    title="新建实体"
    direction="rtl"
    size="520px"
    destroy-on-close
    class="admin-entity-create-drawer"
    @closed="onClosed"
  >
    <el-form
      v-loading="state.loading"
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="100px"
      class="create-form"
    >
      <el-form-item label="图谱" prop="knowledgeGraphId">
        <el-select
          v-model="form.knowledgeGraphId"
          placeholder="选择图谱"
          style="width: 100%"
        >
          <el-option
            v-for="g in state.graphOptions"
            :key="g.value"
            :label="g.label"
            :value="g.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="名称" prop="name">
        <el-input v-model="form.name" />
      </el-form-item>
      <el-form-item label="别名">
        <el-select
          v-model="form.alias"
          multiple
          filterable
          allow-create
          default-first-option
          placeholder="输入后回车添加别名"
          style="width: 100%"
        />
      </el-form-item>
      <el-form-item label="消歧">
        <el-input v-model="form.disambiguation" />
      </el-form-item>
      <el-form-item label="描述">
        <el-input v-model="form.description" type="textarea" :rows="3" />
      </el-form-item>
      <el-form-item label="实体类别">
        <el-select
          v-model="form.typeCodes"
          multiple
          placeholder="选择类别"
          style="width: 100%"
        >
          <el-option
            v-for="c in state.categoryOptions"
            :key="c.code"
            :label="`${c.name} (${c.code})`"
            :value="c.code"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="扩展属性">
        <div
          v-for="(attr, index) in form.attributes"
          :key="index"
          class="attr-row"
        >
          <el-input
            v-model="attr.name"
            placeholder="属性名"
            style="width: 40%"
          />
          <el-input
            v-model="attr.value"
            placeholder="属性值"
            style="width: 40%"
          />
          <el-button link type="danger" @click="removeAttr(index)"
            >删除</el-button
          >
        </div>
        <el-button link type="primary" @click="addAttr">+ 添加属性</el-button>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="state.submitting" @click="submit">
        保存
      </el-button>
    </template>
  </el-drawer>
</template>

<script setup>
import { computed, defineEmits, defineProps, reactive, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { listEnabledGraphs } from "@/api/graphApi";
import { listEntityCategories } from "@/api/adminEntityCategoryApi";
import { createAdminEntity } from "@/api/adminEntityApi";
import { showApiErrorMessage } from "@/api/httpService";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  defaultGraphId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "created"]);

const formRef = ref(null);

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const form = reactive({
  knowledgeGraphId: null,
  name: "",
  alias: [],
  disambiguation: "",
  description: "",
  typeCodes: [],
  attributes: [],
});

const state = reactive({
  loading: false,
  submitting: false,
  graphOptions: [],
  categoryOptions: [],
});

const rules = {
  knowledgeGraphId: [
    { required: true, message: "请选择图谱", trigger: "change" },
  ],
  name: [{ required: true, message: "请输入名称", trigger: "blur" }],
};

function resetForm() {
  form.knowledgeGraphId = props.defaultGraphId ?? null;
  form.name = "";
  form.alias = [];
  form.disambiguation = "";
  form.description = "";
  form.typeCodes = [];
  form.attributes = [];
  formRef.value?.clearValidate();
}

async function loadGraphOptions() {
  const res = await listEnabledGraphs();
  if (res?.code === 0) {
    state.graphOptions = (res.data || []).map((g) => ({
      value: g.id,
      label: g.graphName,
    }));
    if (form.knowledgeGraphId == null && state.graphOptions.length > 0) {
      form.knowledgeGraphId =
        props.defaultGraphId ?? state.graphOptions[0].value;
    }
  }
}

async function loadCategories(graphId) {
  if (graphId == null) {
    state.categoryOptions = [];
    return;
  }
  const res = await listEntityCategories(graphId);
  if (res?.code === 0) {
    state.categoryOptions = res.data || [];
  }
}

function addAttr() {
  form.attributes.push({ name: "", value: "" });
}

function removeAttr(index) {
  form.attributes.splice(index, 1);
}

async function submit() {
  if (!formRef.value) {
    return;
  }
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) {
    return;
  }
  state.submitting = true;
  try {
    const payload = {
      knowledgeGraphId: form.knowledgeGraphId,
      name: form.name.trim(),
      alias: form.alias,
      disambiguation: form.disambiguation,
      description: form.description,
      typeCodes: form.typeCodes,
      attributes: form.attributes.filter((a) => a.name),
    };
    const res = await createAdminEntity(payload);
    if (res?.code === 0) {
      ElMessage.success("保存成功");
      visible.value = false;
      emit("created", res.data?.entityId);
    } else {
      showApiErrorMessage(res);
    }
  } finally {
    state.submitting = false;
  }
}

function onClosed() {
  resetForm();
}

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) {
      return;
    }
    resetForm();
    if (props.defaultGraphId != null) {
      form.knowledgeGraphId = props.defaultGraphId;
    }
    state.loading = true;
    try {
      await loadGraphOptions();
      if (form.knowledgeGraphId != null) {
        await loadCategories(form.knowledgeGraphId);
      }
    } finally {
      state.loading = false;
    }
  }
);

watch(
  () => form.knowledgeGraphId,
  (graphId) => {
    if (visible.value && graphId != null) {
      loadCategories(graphId);
    }
  }
);
</script>

<style lang="scss" scoped>
.create-form {
  .attr-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
}
</style>
